import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Paper,
  CircularProgress,
  useTheme,
  alpha
} from '@mui/material';
import { Work, LocationOn } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useTenant } from '../../context/TenantContext';
import { supabase } from '../../lib/supabaseClient';

interface Job {
  id: string;
  title: string;
  location: string;
  is_remote: boolean;
  salary_min: number | null;
  salary_max: number | null;
  salary_currency: string;
}

export const SimpleHomePage: React.FC = () => {
  const theme = useTheme();
  const { currentTenant, isLoading: tenantLoading } = useTenant();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchJobs = async () => {
      if (!currentTenant) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('jobs')
          .select('id, title, location, is_remote, salary_min, salary_max, salary_currency')
          .eq('entity_id', currentTenant.id)
          .eq('is_public', true)
          .eq('status', 'active')
          .is('deleted_at', null)
          .gte('expiry_date', new Date().toISOString().split('T')[0])
          .order('created_at', { ascending: false })
          .limit(3);

        if (error) throw error;
        setJobs(data || []);
      } catch (err) {
        console.error('Error fetching jobs:', err);
      } finally {
        setLoading(false);
      }
    };

    if (!tenantLoading) {
      fetchJobs();
    }
  }, [currentTenant, tenantLoading]);

  if (loading || tenantLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={50} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Latest Jobs Section */}
      <Box sx={{ py: 10, backgroundColor: theme.palette.background.default }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{
              fontWeight: 700,
              mb: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Latest Job Openings
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
              Explore our current opportunities and find the perfect role for you
            </Typography>
          </Box>

          {jobs.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                No jobs available at the moment
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Check back soon for new opportunities
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
              {jobs.map((job) => (
              <Box key={job.id} sx={{ flex: '1 1 320px', minWidth: '300px' }}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      transform: 'scaleX(0)',
                      transformOrigin: 'left',
                      transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    },
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      '&::before': {
                        transform: 'scaleX(1)',
                      }
                    }
                  }}
                >
                  <CardContent sx={{ p: 3.5 }}>
                    <Typography variant="h5" component="h3" gutterBottom sx={{
                      fontWeight: 700,
                      mb: 3,
                      color: theme.palette.primary.main
                    }}>
                      {job.title}
                    </Typography>

                    <Stack spacing={2.5} sx={{ mb: 3 }}>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box sx={{
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          borderRadius: 2,
                          p: 1,
                          display: 'flex'
                        }}>
                          <LocationOn sx={{ fontSize: 22, color: theme.palette.primary.main }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {job.location}
                          </Typography>
                          {job.is_remote && (
                            <Chip
                              label="Remote"
                              size="small"
                              sx={{
                                mt: 0.5,
                                height: 22,
                                fontSize: '0.7rem',
                                bgcolor: alpha(theme.palette.success.main, 0.1),
                                color: theme.palette.success.dark,
                                fontWeight: 600
                              }}
                            />
                          )}
                        </Box>
                      </Stack>

                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box sx={{
                          backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                          borderRadius: 2,
                          p: 1,
                          display: 'flex'
                        }}>
                         
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {job.salary_min && job.salary_max
                            ? `${job.salary_currency} ${job.salary_min.toLocaleString()} - ${job.salary_max.toLocaleString()}`
                            : 'Competitive'}
                        </Typography>
                      </Stack>
                    </Stack>

                    <Button
                      variant="contained"
                      fullWidth
                      component={Link}
                      to={`/jobs/${job.id}`}
                      startIcon={<Work />}
                      sx={{
                        mt: 2,
                        py: 1.5,
                        borderRadius: 2,
                        fontWeight: 600,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                        }
                      }}
                    >
                      View Details
                    </Button>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
          )}

          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="outlined"
              size="large"
              component={Link}
              to="/jobs"
              sx={{
                px: 5,
                py: 1.5,
                borderRadius: 2,
                borderWidth: 2,
                fontWeight: 600,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  borderWidth: 2,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.2)}`,
                }
              }}
            >
              View All Jobs
            </Button>
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 8, backgroundColor: theme.palette.background.default }}>
        <Container maxWidth="md">
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 3,
              border: `2px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              background: alpha(theme.palette.background.paper, 0.6),
            }}
          >
            <Typography variant="h4" component="h2" gutterBottom sx={{
              fontWeight: 700,
              color: 'text.primary',
              mb: 2
            }}>
              Ready to Join Our Team?
            </Typography>
            <Typography variant="body1" sx={{
              color: 'text.secondary',
              mb: 4,
              fontSize: '1.1rem'
            }}>
              Explore our open positions and take the next step in your career
            </Typography>
            <Button
              variant="contained"
              size="large"
              component={Link}
              to="/jobs"
              sx={{
                px: 5,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                }
              }}
            >
              View All Positions
            </Button>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};